/**
 * 표면 신호 4종 — 포트폴리오 공통 계약. 제품 고유 이벤트는 이 넷 **위에** 얹고
 * 넷을 대체하지 않는다. `app_open`이 없으면 `/pulse`의 discovery 단계가 비어
 * 퍼널 전체가 판정 불가가 된다.
 */
export type SurfaceSignal = 'app_open' | 'landing_view' | 'activation_start' | 'activation_done';

export type ProductEventName = 'share_done' | 'pay_intent' | 'return_visit' | 'case_linked' | 'guide_view' | 'recheck_completed' | 'landing_viewed' | 'case_started' | 'first_receipt_saved' | 'recheck_scheduled' | 'export_completed' | 'delete_completed' | 'recovery_failed';

export type EventName = SurfaceSignal | ProductEventName;

const SURFACE_SIGNALS: readonly SurfaceSignal[] = ['app_open', 'landing_view', 'activation_start', 'activation_done'];

export function isEventName(value: unknown): value is EventName {
  return typeof value === 'string' && (
    (SURFACE_SIGNALS as readonly string[]).includes(value)
    || ['share_done', 'pay_intent', 'return_visit', 'case_linked', 'guide_view', 'recheck_completed', 'landing_viewed', 'case_started', 'first_receipt_saved', 'recheck_scheduled', 'export_completed', 'delete_completed', 'recovery_failed'].includes(value)
  );
}
export interface SafeEvent {
  name: EventName;
  productId: 'coverage-timeline';
  channel: 'web' | 'apps_in_toss';
  buildVersion: string;
  resultCode: 'ok' | 'cancelled' | 'invalid' | 'failed';
  occurredAtDay: string;
  variant?: string;
}

export const EVENT_VARIANTS: Partial<Record<EventName, readonly string[]>> = {
 activation_done: ['landing','guide','share_target','bulk','direct'],
 guide_view: ['correction-request','correction-vs-rebuttal','article-deletion','save-article-before-deleted','news-scrap','check-article-edit-time','correction-deadline'],
 export_completed: ['html','csv','backup','claim_packet'], share_done:['text'], pay_intent:['claim_packet_second','price_page'],
 return_visit:['day7plus'], case_linked:['follow_up','update','correction','rebuttal','withdrawal'], delete_completed:['all','case'],
};
const FORBIDDEN_KEYS = new Set(['url', 'topic', 'publisher', 'person', 'caseTitle', 'claimText', 'memo', 'shareToken', 'filename', 'query', 'text']);

export function assertSafeTelemetry(payload: Record<string, unknown>): void {
  for (const key of Object.keys(payload)) {
    if (FORBIDDEN_KEYS.has(key)) throw new Error('FORBIDDEN_ANALYTICS_FIELD');
  }
}

export function createSafeEvent(name: EventName, resultCode: SafeEvent['resultCode'], channel: SafeEvent['channel'], variant?: string): SafeEvent {
  const event: SafeEvent = {
    name, productId: 'coverage-timeline', channel, buildVersion: __BUILD_VERSION__, resultCode,
    ...(variant && EVENT_VARIANTS[name]?.includes(variant) ? {variant} : {}),
    occurredAtDay: new Date().toISOString().slice(0, 10),
  };
  assertSafeTelemetry(event as unknown as Record<string, unknown>);
  return event;
}

/**
 * 수집 경로.
 *
 * `/api/*`는 이 제품에서 fail-closed 경계다(Worker `isDenied` · 공개 사건 API 금지).
 * 그 경계를 계측 때문에 뚫지 않으려고 전용 경로를 쓴다.
 *
 * Web은 같은 출처, Toss 정적 번들은 공개 Worker의 전용 수집 경로를 사용한다.
 * 어느 채널에서도 사건 기록·기사 URL·메모를 전송하지 않는다.
 */
const ENDPOINT = '/telemetry';
const TOSS_ENDPOINT = 'https://bodochai.com/telemetry';

/** 같은 페이지 로드에서 같은 신호를 두 번 보내지 않는다. */
const sent = new Set<string>();

/**
 * 2026-08-11 실측: `https://bodochai.com` 첫 방문 9.5초
 * 동안 계측 요청 0건이었다.
 *
 * 원인이 미묘하다 — **계약은 완비돼 있고 전송기와 호출자가 없었다.** 이벤트 이름,
 * 금지 필드 가드(`assertSafeTelemetry`), 안전한 이벤트 팩토리(`createSafeEvent`)가
 * 전부 있었지만 `createSafeEvent`의 호출자가 저장소 전체에 0건이었고 payload를
 * 어디로도 보내지 않았다. 파일만 열어 보면 계측이 있는 제품으로 보인다.
 */
export function sendEvent(
  name: EventName,
  resultCode: SafeEvent['resultCode'],
  channel: SafeEvent['channel'],
  variant?: string,
): void {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return;
  const key = `${name}:${variant ?? ''}`;
  if (sent.has(key)) return;
  sent.add(key);
  let event: SafeEvent;
  try {
    event = createSafeEvent(name, resultCode, channel, variant);
  } catch {
    // 금지 필드 가드에 걸리면 보내지 않는다.
    return;
  }
  try {
    void fetch(channel === 'apps_in_toss' ? TOSS_ENDPOINT : ENDPOINT, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(event),
      credentials: 'omit',
      // 이탈 직전 전송이 브라우저에 죽지 않게 한다.
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // 계측 실패가 제품 동작을 막지 않는다.
  }
}

/**
 * Worker 쪽 검증. URL·주제·언론사·메모 같은 금지 값이 섞여 들어오면 거부한다.
 */
export function parseSafeEvent(payload: unknown): SafeEvent | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const candidate = payload as Record<string, unknown>;
  try {
    assertSafeTelemetry(candidate);
  } catch {
    return null;
  }
  if (!isEventName(candidate.name)) return null;
  if (candidate.productId !== 'coverage-timeline') return null;
  if (candidate.channel !== 'web' && candidate.channel !== 'apps_in_toss') return null;
  if (typeof candidate.buildVersion !== 'string' || candidate.buildVersion.length > 80) return null;
  const results = ['ok', 'cancelled', 'invalid', 'failed'];
  if (typeof candidate.resultCode !== 'string' || !results.includes(candidate.resultCode)) return null;
  if (typeof candidate.occurredAtDay !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(candidate.occurredAtDay)) return null;
  return {
    ...(typeof candidate.variant === 'string' && EVENT_VARIANTS[candidate.name]?.includes(candidate.variant) ? {variant:candidate.variant} : {}),
    name: candidate.name,
    productId: 'coverage-timeline',
    channel: candidate.channel,
    buildVersion: candidate.buildVersion,
    resultCode: candidate.resultCode as SafeEvent['resultCode'],
    occurredAtDay: candidate.occurredAtDay,
  };
}
